import { Colli } from '@frontend/colli/types';
import { EntityType } from '@frontend/common';
import { Package, PackageType } from '@frontend/package/types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { TransactionDetail } from '../transaction-detail-modal.controller';
import { buildSlotParentIds } from '../transaction-detail-slot-parents';
import { PackageProofStateBadge } from './package-proof-state-badge.component';
import { ProofOfIdentityInfoIcon } from './proof-of-identity-info-icon.component';
import { ProofOfIdentityColliGroup } from './proof-of-identity-packages.utils';
import { useProofOfIdentityCard } from './use-proof-of-identity-card';

function ActionTypeLabel({ type }: { type: PackageType }) {
    switch (type) {
        case PackageType.DROP_OFF:
            return (
                <FormattedMessage
                    id='platform.transaction.modal.proof-of-identity.action-drop-off'
                    description='End-user label for a drop-off action that requires proof of identity (a parcel will be dropped off)'
                    defaultMessage='Drop off'
                />
            );
        case PackageType.PICK_UP:
            return (
                <FormattedMessage
                    id='platform.transaction.modal.proof-of-identity.action-pick-up'
                    description='End-user label for a pick-up action that requires proof of identity (a parcel will be picked up)'
                    defaultMessage='Pick up'
                />
            );
        case PackageType.AUDIT:
            return (
                <FormattedMessage
                    id='platform.transaction.modal.proof-of-identity.package-type-audit'
                    description='Human-readable label for an audit package type on the proof-of-identity card'
                    defaultMessage='Audit'
                />
            );
        case PackageType.PASS_THROUGH:
            return (
                <FormattedMessage
                    id='platform.transaction.modal.proof-of-identity.package-type-pass-through'
                    description='Human-readable label for a pass-through package type on the proof-of-identity card'
                    defaultMessage='Pass-through'
                />
            );
        case PackageType.REMOVAL:
            return (
                <FormattedMessage
                    id='platform.transaction.modal.proof-of-identity.package-type-removal'
                    description='Human-readable label for a removal package type on the proof-of-identity card'
                    defaultMessage='Removal'
                />
            );
        default:
            return type;
    }
}

function PackageProofRow({ pkg }: { pkg: Package }) {
    const hasLocation = Boolean(pkg.spot_id || pkg.slot_id);
    return (
        <div className='inline-flex w-full flex-wrap items-center justify-between gap-3 self-stretch'>
            <div className='inline-flex flex-wrap items-center gap-2'>
                <div className='text-foreground-default text-base font-medium'>
                    <ActionTypeLabel type={pkg.type} />
                </div>
                {hasLocation && (
                    <div className='inline-flex flex-wrap items-center gap-2'>
                        {pkg.spot_id && (
                            <PlatformObjectBadge
                                id={pkg.spot_id}
                                entityType={EntityType.SPOT}
                            />
                        )}
                        {pkg.slot_id && (
                            <PlatformObjectBadge
                                id={pkg.slot_id}
                                entityType={EntityType.SLOT}
                                parentIds={buildSlotParentIds({ spotId: pkg.spot_id, moduleId: pkg.module_id })}
                            />
                        )}
                    </div>
                )}
            </div>
            <PackageProofStateBadge state={pkg.state} />
        </div>
    );
}

function PackageProofList({ packages }: { packages: Package[] }) {
    return (
        <div className='inline-flex w-full flex-col items-start justify-start gap-3 self-stretch'>
            {packages.map((pkg) => (
                <PackageProofRow
                    key={pkg.id}
                    pkg={pkg}
                />
            ))}
        </div>
    );
}

function PackageProofGroups({ groups }: { groups: ProofOfIdentityColliGroup[] }) {
    return (
        <div className='inline-flex w-full flex-col items-start justify-start gap-4 self-stretch'>
            {groups.map((group) => (
                <div
                    key={group.colliId ?? 'ungrouped'}
                    className='inline-flex w-full flex-col items-start justify-start gap-2 self-stretch'>
                    <div className='text-foreground-default text-base font-semibold'>
                        {group.label ?? (
                            <FormattedMessage
                                id='platform.transaction.modal.proof-of-identity.ungrouped'
                                description='Fallback group header for packages without a colli on the proof-of-identity card'
                                defaultMessage='Other'
                            />
                        )}
                    </div>
                    <PackageProofList packages={group.packages} />
                </div>
            ))}
        </div>
    );
}

export interface ProofOfIdentityCardProps {
    transaction: TransactionDetail;
    collis: Colli[] | null;
}

export const ProofOfIdentityCard = ({ transaction, collis }: ProofOfIdentityCardProps) => {
    const { shouldShow, packagesRequiringProof, groupByColli, groups } = useProofOfIdentityCard(transaction, collis);

    if (!shouldShow) return null;

    return (
        <div className='bg-background-surface outline-border-default inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
            <div className='inline-flex w-full items-center gap-2'>
                <div className='text-foreground-default min-w-0 flex-1 justify-start text-xl font-semibold'>
                    <FormattedMessage
                        id='platform.transaction.modal.proof-of-identity.title'
                        description='Title of the proof-of-identity card on the platform transaction detail modal'
                        defaultMessage='Proof of identity'
                    />
                </div>
                <ProofOfIdentityInfoIcon />
            </div>
            {groupByColli ? <PackageProofGroups groups={groups} /> : <PackageProofList packages={packagesRequiringProof} />}
        </div>
    );
};
